// Client for the opt-in report routes of the backend (lud-backend/docs/architecture.md, Reporting and analysis). One place builds the parameters, so every
// screen sends the same filter to the page, the summary, the option lists, the id list and the export.
//
//   filters: { stores: [code|id], month: "YYYY-MM", from, to, apps: [key], branches: [name], branchRefs: [id|null], page, limit, fields: [name] }
//
// Every function resolves to { ok, status, data } (see http.js). `callWithFallback` implements the plan's rule: use the new route, and only when the
// backend does not know it (404 / 405 / 400, or an answer without the expected shape) fall back to the legacy call; real errors (401/403/5xx/network) stay errors.
import { saveAs } from "file-saver";
import { apiDelete, apiGet } from "./http";

export const REPORT_KINDS = Object.freeze(["daily", "monthly", "compensation"]);
export const EXPORT_NAMES = Object.freeze({ daily: "Daily", monthly: "Monthly", compensation: "Details" });
const FALLBACK_STATUSES = [400, 404, 405];

function assertKind(kind) {
  if (!REPORT_KINDS.includes(kind)) throw new Error(`Unknown report kind: ${kind}`);
}

// UI filter object -> API parameters. `branches` is sent as `branchName` (the legacy compensation route owns `branch`).
export function reportParams(filters = {}) {
  const csv = (list) => (Array.isArray(list) && list.length ? list.join(",") : undefined);
  return {
    sId: filters.stores && filters.stores.length ? filters.stores : undefined,
    month: filters.month || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    app: csv(filters.apps),
    branchName: csv(filters.branches),
    branchRef: csv(filters.branchRefs && filters.branchRefs.map((ref) => ref ?? "none")),
    page: filters.page || undefined,
    limit: filters.limit || undefined,
    fields: csv(filters.fields),
    orderStatus: csv(filters.orderStatuses),
    cause: csv(filters.causes),
    sort: filters.sort || undefined,
    dir: filters.sort ? filters.dir || undefined : undefined,
    groupBy: csv(filters.groupBy),
  };
}

const route = (kind, suffix = "") => {
  assertKind(kind);
  return `report/${kind}${suffix}`;
};

// Paged rows (when filters.page or filters.limit is set) or the analysis window (period filters only).
export const listReport = (kind, filters, options) => apiGet(route(kind), reportParams(filters), options);
// Per-store totals for the same filters (never limited by a page size).
// With filters.groupBy (["month","app","branch"]) the answer also carries `groups`: the same totals per header of the expanded rows.
export const reportSummary = (kind, filters, options) => apiGet(route(kind, "/summary"), reportParams({ ...filters, page: undefined, limit: undefined, fields: undefined, sort: undefined }), options);
// Apps / stores / branches available inside the caller's scope and period (app and branch filters are ignored by the server on purpose).
export const reportOptions = (kind, filters, options) => apiGet(route(kind, "/filter-options"), reportParams({ ...filters, page: undefined, limit: undefined, fields: undefined, sort: undefined, groupBy: undefined }), options);
// Ids (with their store) of every row matching the filters; feeds the existing bulk delete. Needs the report.bulkDelete permission.
export const reportIds = (kind, filters, options) => apiGet(route(kind, "/ids"), reportParams({ ...filters, page: undefined, limit: undefined, fields: undefined, sort: undefined, groupBy: undefined }), options);

// Every row of a small node (a Daily day, a Monthly month), page after page, so a node is never cut in half. Same answer shape as listReport.
const ALL_ROWS_PAGE = 100;
const ALL_ROWS_MAX_PAGES = 20;
export async function listAllReportRows(kind, filters, options) {
  const rows = [];
  for (let page = 1; page <= ALL_ROWS_MAX_PAGES; page += 1) {
    const result = await listReport(kind, { ...filters, page, limit: ALL_ROWS_PAGE }, options);
    if (!result.ok) return result;
    rows.push(...(result.data.data || []));
    if (!result.data.pagination || page >= result.data.pagination.totalPages) return { ...result, data: { ...result.data, data: rows, pagination: null } };
  }
  return { ok: false, status: 413, data: null };
}

// Confirmed filtered delete: exactly the previewed rows, inside the scope they were previewed under (`scope`: reportParams of the applied filters).
export const deleteReportRows = (kind, ids, scope, options) => apiDelete(route(kind, "/by-ids"), { ids, scope }, options);

// Downloads the whole filtered set as .xlsx. The token travels in the header, so a plain link cannot be used.
export async function downloadReportExport(kind, filters, { token, signal, save = saveAs } = {}) {
  const result = await apiGet(route(kind, "/export"), reportParams({ ...filters, page: undefined, limit: undefined, fields: undefined, groupBy: undefined }), { token, signal, as: "blob" });
  if (!result.ok) return result;
  const disposition = (result.headers && result.headers.get && result.headers.get("content-disposition")) || "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  save(result.data, match ? match[1] : `${EXPORT_NAMES[kind]}-${Date.now()}.xlsx`);
  return { ok: true, status: result.status, data: null };
}

// Shape checks used by callWithFallback (a backend without the new routes answers the legacy body).
export const isPagedAnswer = (data) => Boolean(data && Array.isArray(data.data) && data.pagination && typeof data.pagination.total === "number");
export const isSummaryAnswer = (data) => Boolean(data && Array.isArray(data.stores) && data.totals);
export const isOptionsAnswer = (data) => Boolean(data && Array.isArray(data.apps) && Array.isArray(data.stores));

// primary(): new route; detect(data): true when the answer has the new shape; fallback(): the legacy call, same return type.
// Resolves to { mode: "new" | "legacy", result }.
export async function callWithFallback({ primary, detect, fallback }) {
  const first = await primary();
  if (first.ok && detect(first.data)) return { mode: "new", result: first };
  const unknownRoute = !first.ok && FALLBACK_STATUSES.includes(first.status);
  if ((first.ok && !detect(first.data)) || unknownRoute) {
    const legacy = await fallback();
    return { mode: "legacy", result: legacy };
  }
  return { mode: "new", result: first }; // a real error: let the screen show it
}
